import React from "react";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import PaymentsIcon from "@mui/icons-material/Payments";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import type { Partner } from "@kappa-v2/contracts";
import type { RowActionItem } from "../../../components/DataGridRowActionsMenu.js";

export interface PartnerRowActionHandlers {
  onEdit: (p: Partner) => void;
  onDelete: (p: Partner) => void;
  onToggleActive: (p: Partner) => void;
  /** Apre l'iscrizione guidata (servizi ricorrenti, scheda del mese, pagamento). */
  onEnroll?: (p: Partner) => void;
  /** Naviga a una rotta (es. `/protected/credits?personId=...`). Se assente, i CTA di navigazione non vengono mostrati. */
  onNavigate?: (path: string) => void;
}

/** Traduttore minimale (compatibile con la `t` di react-i18next). */
type Translate = (key: string) => string;

export function buildPartnerRowActionItems(
  row: Partner,
  actions: PartnerRowActionHandlers,
  t: Translate
): RowActionItem[] {
  const active = row.active;
  const id = row._id ?? "";
  const nav = actions.onNavigate;

  const items: RowActionItem[] = [
    {
      key: "edit",
      label: t("partners.actions.edit"),
      description: t("partners.actions.editDesc"),
      icon: <EditIcon fontSize="small" />,
      onClick: () => actions.onEdit(row)
    }
  ];

  if (actions.onEnroll) {
    const onEnroll = actions.onEnroll;
    items.push({
      key: "enroll",
      label: t("partners.actions.enroll"),
      description: t("partners.actions.enrollDesc"),
      icon: <HowToRegIcon fontSize="small" />,
      disabled: !active,
      onClick: () => onEnroll(row)
    });
  }

  if (nav) {
    items.push(
      {
        key: "credits",
        label: t("partners.actions.credits"),
        description: t("partners.actions.creditsDesc"),
        icon: <RequestQuoteIcon fontSize="small" />,
        disabled: !active,
        onClick: () => nav(`/protected/credits?personId=${id}`)
      },
      {
        key: "payments",
        label: t("partners.actions.payments"),
        description: t("partners.actions.paymentsDesc"),
        icon: <PaymentsIcon fontSize="small" />,
        disabled: !active,
        onClick: () => nav(`/protected/debits?personId=${id}`)
      },
      {
        key: "receipts",
        label: t("partners.actions.receipts"),
        description: t("partners.actions.receiptsDesc"),
        icon: <ReceiptLongIcon fontSize="small" />,
        disabled: !active,
        onClick: () => nav(`/protected/receipts?personId=${id}`)
      },
      {
        key: "services",
        label: t("partners.actions.services"),
        description: t("partners.actions.servicesDesc"),
        icon: <SubscriptionsIcon fontSize="small" />,
        disabled: !active,
        onClick: () => nav(`/protected/partner-services?personId=${id}`)
      }
    );
  }

  items.push(
    {
      key: "toggle",
      label: active ? t("partners.actions.deactivate") : t("partners.actions.activate"),
      description: active ? t("partners.actions.deactivateDesc") : t("partners.actions.activateDesc"),
      icon: <PowerSettingsNewIcon fontSize="small" />,
      dividerBefore: true,
      onClick: () => actions.onToggleActive(row)
    },
    {
      key: "delete",
      label: t("partners.actions.delete"),
      description: t("partners.actions.deleteDesc"),
      icon: <DeleteIcon fontSize="small" />,
      destructive: true,
      onClick: () => actions.onDelete(row)
    }
  );

  return items;
}
